import { IControl } from '@Common/Controls/Controls';
import { FilterField } from '@Common/Controls/FilterField';
import { ScrollablePanel } from '@Common/Controls/ScrollablePanel';
import constants from '@Common/Core/Constant';
import { IDestroyable, safeDestroys } from '@Common/Core/Destroyable';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { FilterType, TypeKind } from '@Common/Enums';
import formatHelper from '@Common/Helpers/FormatHelper';
import { FieldCondition } from '@InstanceFactory';
import { filterHelper } from '@View/Helpers/FilterHelper';

export type Attribute = {
    id: string;
    name: string;
    type: TypeKind;
    formatName?: string;
};

export class ConditionControl implements IControl, IDestroyable {
    private readonly table: JQuery;
    private readonly root: ScrollablePanel;
    private items: FilterField[] = [];
    public onChange = MulticastDelegate.create<ICallback<any>>();
    public onDataRequested = MulticastDelegate.create<(dimensionId: string, startResultIndex: number, pageSize: number) => any>();

    constructor(private readonly param) {
        param = _.defaults(param, {
            css: null,
            className: '',
            width: 'auto',
            height: '350px',

            inputWidth: 120,
            readonly: false
        });

        this.root = new ScrollablePanel({
            height: param.height,
            width: param.width,
            hScroll: false
        });

        const content = $('<div/>');
        this.root.append(content);
        this.table = $(`
<table>
    <tbody>
        <tr>
            <th style='height:0px; width:auto; min-width:150px'></th>
            <th style='height:0px; width:100px'></th>
            <th style='height:0px; width:auto'></th>
        </tr>
    </tbody>
</table>`);

        content.append(this.table);
    }

    public getValue(): FieldCondition[] {
        return this.items.map(item => item.getValue()).filter(item => item.operatorType !== FilterType.unknown);
    }

    private clearTable(): void {
        safeDestroys(...this.items);
        ConditionControl.clearTable(this.table);
        this.items = [];
    }

    public setValue(attributes: Attribute[], conditions: FieldCondition[]): void {
        this.clearTable();

        _(attributes)
            .reject(attribute => attribute.id === constants.process.violator || attribute.id === constants.process.isFinished)
            .forEach(attribute => {
                const operators = filterHelper.getFilterTypes(attribute.type, true);
                const tr = $('<tr><td></td><td></td><td></td></tr>');
                ConditionControl.addRow(tr, this.table);
                if (!attribute.formatName) {
                    attribute.formatName = constants.defaultNamedFormatName;
                }
                let showTime = false;
                if (attribute.type === TypeKind.dateTime) {
                    const format = formatHelper.getDateTimeFormat(attribute.formatName);
                    showTime = format.withTime;
                }
                const filterField = new FilterField({
                    fieldType: attribute.type,
                    label: { content: attribute.name, width: '170px', styleName: 'gap-2-right' },
                    operator: { items: operators, show: !this.param.readonly, width: '100px' },
                    input: {
                        width: this.param.inputWidth,
                        canEdit: !this.param.readonly,
                        showTime: showTime
                    }
                });

                const currentCondition = conditions.find(condition => condition.fieldPath === attribute.id);
                if (currentCondition) {
                    filterField.setValue(currentCondition);
                } else {
                    const condition = FieldCondition.create();
                    condition.fieldPath = attribute.id;
                    condition.operatorType = FilterType.unknown;
                    condition.values = ['', ''];
                    filterField.setValue(condition);
                }

                this.items.push(filterField);
                const firstCell = tr.find(':nth-child(1)');
                const secondCell = tr.find(':nth-child(2)');
                const thirdCell = tr.find(':nth-child(3)');
                firstCell.append(filterField.getLabelCell());
                secondCell.append(filterField.getOperatorCell());
                thirdCell.append(filterField.getValueCell());

                filterField.onChange(value => {
                    const val = this.getValue();
                    this.onChange(val);
                });

                filterField.onDataRequested((startResultIndex, pageSize) => this.onDataRequested(attribute.id, startResultIndex, pageSize));
            });

        this.onChange(this.getValue());
    }

    public toStr(): string {
        const filters = [];
        for (let i = 0; i < this.items.length; i++) {
            const itemStr = this.items[i].toStr();
            if (itemStr) {
                filters.push(itemStr);
            }
        }
        return filters.join(';');
    }

    public empty(): void {
        ConditionControl.clearTable(this.table);
    }

    private static clearTable($table) {
        $('tr:not(:first-child)', $table).remove();
    }

    private static addRow($tr, $table) {
        const $tbody = $table.find('tbody');
        $tbody.append($tr);
    }

    public getControl(): JQuery {
        return this.root.getControl();
    }

    public adjust(w?, h?): void {
        this.root.adjust(w, h);
    }

    public enabled(value?: boolean): boolean {
        return true;
    }

    public setState(enabled: boolean): void {
        this.enabled(enabled);
    }

    public visible(value?: boolean): boolean {
        return true;
    }

    public readonly(readonly?: boolean): boolean {
        return false;
    }

    public destroy(): void {
        this.clearTable();
        safeDestroys(this.root, this.onChange, this.onDataRequested);
    }
}
